/**
 * Shared match-confidence scoring — how well a set of catalog candidates
 * matches a known (name, set, number) input.
 *
 * Two consumers, one definition of "confident":
 *   - CSV import wizard (TNK-140) — per-row confidence pill on the Review
 *     step, scoring catalog enrichment against the operator's raw CSV columns.
 *   - Card scan resolver (TNK-194) — scoring the catalog printings a scan
 *     resolved to against the model's name + number read, to decide whether
 *     a scan auto-accepts (high) or is flagged for review (medium/low).
 *
 * Lives in @tcg-web/normalize because both apps/web (import) and apps/api
 * (scan) import it; it is pure and dependency-free.
 *
 * Score breakdown:
 *   - Name similarity (Levenshtein-normalized) → up to 50 pts
 *   - Set name match (case-insensitive, partial credit for fuzzy) → 30 pts
 *   - Card number match (after stripping leading zeros + total suffix) → 20 pts
 *   - Catalog hit absent → returns 0 regardless of fields
 *
 * Thresholds (confidenceTone maps these to a band the UI colors):
 *   - ≥ 90  high    (confident match)
 *   - ≥ 75  medium  (probable — operator should glance)
 *   - <  75  low     (uncertain — operator should review + override)
 */

export interface MatchConfidenceInputs {
  /** Original CSV name (operator's input). */
  csvName: string | undefined;
  /** Original CSV set name. */
  csvSetName: string | undefined;
  /** Original CSV card number — may be "001/198" or similar. */
  csvCardNumber: string | undefined;
  /** Parsed name post-enrichment (== catalog match if hit). */
  parsedName: string | undefined;
  /** Parsed set name post-enrichment. */
  parsedSetName: string | undefined;
  /** Parsed card number post-enrichment. */
  parsedCardNumber: string | undefined;
  /** True if enrichment attached at least one catalog signal —
   *  imageFrontRef, tcgSupertype, tcgArtist, tcgTypes, etc. */
  hasCatalogHit: boolean;
}

const NAME_WEIGHT = 50;
const SET_WEIGHT = 30;
const NUMBER_WEIGHT = 20;

export function computeMatchConfidence(inputs: MatchConfidenceInputs): number {
  if (!inputs.hasCatalogHit) return 0;

  let score = 0;

  if (inputs.csvName && inputs.parsedName) {
    score += NAME_WEIGHT * nameSimilarity(inputs.csvName, inputs.parsedName);
  } else if (inputs.parsedName) {
    // No CSV name to compare — the catalog still gave us a name. Half
    // credit; we can't verify it matches the operator's intent.
    score += NAME_WEIGHT / 2;
  }

  if (inputs.csvSetName && inputs.parsedSetName) {
    if (
      normalizeText(inputs.csvSetName) === normalizeText(inputs.parsedSetName)
    ) {
      score += SET_WEIGHT;
    } else {
      // Partial credit for fuzzy set match — operator may have abbreviated.
      score +=
        SET_WEIGHT *
        0.5 *
        nameSimilarity(inputs.csvSetName, inputs.parsedSetName);
    }
  } else if (inputs.parsedSetName) {
    score += SET_WEIGHT / 2;
  }

  if (inputs.csvCardNumber && inputs.parsedCardNumber) {
    if (
      normalizeCardNumber(inputs.csvCardNumber) ===
      normalizeCardNumber(inputs.parsedCardNumber)
    ) {
      score += NUMBER_WEIGHT;
    }
  } else if (inputs.parsedCardNumber) {
    score += NUMBER_WEIGHT / 2;
  }

  return Math.max(0, Math.min(100, Math.round(score)));
}

/**
 * Score a pool of candidates against the same input row, then apply a
 * uniqueness boost ONLY to the single clear top match.
 *
 * The shape we want: if two candidates have the same name but one matches
 * the input's set + number and the other doesn't, the one that matches
 * should rank conspicuously higher. With the boost applied only to the
 * unique top:
 *
 *   - top candidate (base 100) → 100
 *   - runner-up   (base 50)   → 50    (no promotion)
 *
 * Pool of 1, base ≥ 50 → top is the only answer → boost to 100.
 * Ties at the top (two candidates with identical bases) → no boost on
 * either — they are indistinguishable on the inputs given, so the caller
 * should treat the match as uncertain and review manually.
 */
export interface ScoreCandidateInputs {
  csv: {
    name?: string;
    setName?: string;
    cardNumber?: string;
  };
  candidates: Array<{
    name: string;
    setName: string;
    number: string;
  }>;
}

export function scoreCandidatePool(inputs: ScoreCandidateInputs): number[] {
  const bases = inputs.candidates.map((c) =>
    computeMatchConfidence({
      csvName: inputs.csv.name,
      csvSetName: inputs.csv.setName,
      csvCardNumber: inputs.csv.cardNumber,
      parsedName: c.name,
      parsedSetName: c.setName,
      parsedCardNumber: c.number,
      hasCatalogHit: true,
    }),
  );
  if (bases.length === 0) return [];

  const top = Math.max(...bases);
  const topCount = bases.filter((b) => b === top).length;
  const poolSize = bases.length;

  return bases.map((base) => {
    // Boost only the single clear top match. Runners-up + tied tops
    // stay at their base score so the visual gap between "this is
    // clearly the answer" vs "this is plausible" is preserved.
    if (base !== top || topCount > 1 || base < 50) return base;
    const ambiguity = (poolSize - 1) / poolSize;
    const bonus = (1 - ambiguity) * (100 - base);
    return Math.min(100, Math.round(base + bonus));
  });
}

export function confidenceTone(score: number): "high" | "medium" | "low" {
  if (score >= 90) return "high";
  if (score >= 75) return "medium";
  return "low";
}

function normalizeText(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Strip leading zeros + total ("/198") so "001/198" and "1" both
 * resolve to "1" for matching purposes.
 */
function normalizeCardNumber(s: string): string {
  const num = s.split("/")[0].trim();
  if (/^\d+$/.test(num)) return String(parseInt(num, 10));
  return num.toLowerCase();
}

/**
 * 0–1 similarity score from Levenshtein distance, normalized by the
 * longer string's length. 1 = exact match, 0 = totally different.
 */
function nameSimilarity(a: string, b: string): number {
  const aNorm = normalizeText(a);
  const bNorm = normalizeText(b);
  if (aNorm === bNorm) return 1;
  if (!aNorm || !bNorm) return 0;
  const maxLen = Math.max(aNorm.length, bNorm.length);
  const dist = levenshtein(aNorm, bNorm);
  return Math.max(0, 1 - dist / maxLen);
}

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  // Rolling 2-row DP — O(min(m,n)) memory.
  let prev = new Array<number>(n + 1);
  let curr = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}
